// Shared document building blocks (prototype `components/Documents.tsx`): line editor, totals,
// status stepper, side panels, read-only lines and journal blocks. Used by purchasing, sales and
// invoicing pages.
import { money, moneyBase, qty, type VariantOption } from "@keel/core";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Select } from "./Select";
import { Icon } from "./ui";

export interface EditLine {
  key: number;
  variantId: string;
  description: string;
  quantity: number | string;
  unitPrice: number | string;
  discountPercent: number | string;
  taxRate: number | string;
  /**
   * The rate was chosen rather than guessed. Only a chosen rate is sent: otherwise the server's
   * VAT posting setup decides, and the rate shown here is the product card's, until it is saved.
   */
  taxRateChosen?: boolean;
  accountCode?: string;
}

let lineKey = 0;
export const blankLine = (tax = 21): EditLine => ({
  key: ++lineKey,
  variantId: "",
  description: "",
  quantity: 1,
  unitPrice: 0,
  discountPercent: 0,
  taxRate: tax,
});
/** A line already on a document: its rate was settled when it was saved, so it is kept. */
export const withKey = (l: Omit<EditLine, "key">): EditLine => ({
  taxRateChosen: true,
  ...l,
  key: ++lineKey,
});

const net = (l: EditLine) =>
  Math.round(
    Number(l.quantity || 0) * Number(l.unitPrice || 0) * (1 - Number(l.discountPercent || 0) / 100) * 100,
  ) / 100;

/** Lines as the API's `LineInput`. Empty lines are dropped. */
export const toInput = (lines: EditLine[]) =>
  lines
    .filter((l) => l.variantId || l.description.trim())
    .map((l) => ({
      variantId: l.variantId || null,
      description: l.description || null,
      quantity: Number(l.quantity),
      unitPrice: Number(l.unitPrice),
      discountPercent: Number(l.discountPercent) || 0,
      taxRate: l.taxRateChosen ? Number(l.taxRate) : null,
      accountCode: l.accountCode || null,
    }));

/** Editable document lines. Picking a variant fills description, price (or cost) and VAT. */
export function LineEditor({
  lines,
  onChange,
  variants,
  purchase,
  stock,
  taxRates = [21, 24, 12, 9, 5, 0],
  zeroVat,
}: {
  lines: EditLine[];
  onChange: (l: EditLine[]) => void;
  variants: VariantOption[];
  purchase?: boolean;
  stock?: Record<string, number>;
  taxRates?: number[];
  /** Reverse charge or export: every line is at 0% (the server enforces it); shown as the reason. */
  zeroVat?: string;
}) {
  const rate = (l: EditLine) => (zeroVat ? 0 : Number(l.taxRate));
  const set = (key: number, patch: Partial<EditLine>) =>
    onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const groups = variants.reduce<Record<string, VariantOption[]>>((g, v) => {
    const k = v.category ?? "Other";
    g[k] = g[k] ?? [];
    g[k].push(v);
    return g;
  }, {});
  return (
    <>
      <div className="lines-wrap">
        <table className="lines editing">
          <thead>
            <tr>
              <th className="c-prod">Item</th>
              <th className="r c-qty">Qty</th>
              <th className="r c-price">{purchase ? "Unit cost" : "Unit price"}</th>
              <th className="r c-qty">Disc %</th>
              <th className="r c-tax">VAT</th>
              <th className="r c-total">Amount</th>
              <th className="c-x" />
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => {
              const v = variants.find((x) => x.id === l.variantId);
              const avail = v && stock ? stock[v.id] : undefined;
              const short = !purchase && avail !== undefined && avail < Number(l.quantity);
              return (
                <tr key={l.key}>
                  <td className="c-prod">
                    <Select
                      value={l.variantId}
                      onChange={(e) => {
                        const nv = variants.find((x) => x.id === e.target.value);
                        set(
                          l.key,
                          nv
                            ? {
                                variantId: nv.id,
                                description: nv.title,
                                unitPrice: purchase ? nv.cost : nv.price,
                                taxRate: nv.taxRate,
                                taxRateChosen: false,
                              }
                            : { variantId: "" },
                        );
                      }}
                    >
                      <option value="">Free-text line…</option>
                      {Object.entries(groups).map(([cat, vs]) => (
                        <optgroup key={cat} label={cat}>
                          {vs.map((x) => (
                            <option key={x.id} value={x.id}>
                              {x.sku} — {x.title}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </Select>
                    <input
                      value={l.description}
                      onChange={(e) => set(l.key, { description: e.target.value })}
                      placeholder="Description"
                    />
                    {v && !purchase && v.isStocked && avail !== undefined && (
                      <small className={short ? "tone-warn" : "muted"}>
                        {qty(avail)} available{short ? " — rest will be backordered" : ""}
                      </small>
                    )}
                  </td>
                  <td className="c-qty">
                    <input
                      className="r"
                      type="number"
                      min="0"
                      step="any"
                      value={l.quantity}
                      onChange={(e) => set(l.key, { quantity: e.target.value })}
                    />
                  </td>
                  <td className="c-price">
                    <input
                      className="r"
                      type="number"
                      min="0"
                      step="0.01"
                      value={l.unitPrice}
                      onChange={(e) => set(l.key, { unitPrice: e.target.value })}
                    />
                  </td>
                  <td className="c-qty">
                    <input
                      className="r"
                      type="number"
                      min="0"
                      max="100"
                      step="any"
                      value={l.discountPercent}
                      onChange={(e) => set(l.key, { discountPercent: e.target.value })}
                    />
                  </td>
                  <td className="c-tax">
                    {zeroVat ? (
                      <span className="muted" title={zeroVat}>
                        0%
                      </span>
                    ) : (
                      <Select
                        className="r"
                        value={String(Number(l.taxRate))}
                        onChange={(e) => set(l.key, { taxRate: e.target.value, taxRateChosen: true })}
                      >
                        {[...new Set([...taxRates, Number(l.taxRate)])].map((r) => (
                          <option key={r} value={String(r)}>
                            {r}%
                          </option>
                        ))}
                      </Select>
                    )}
                  </td>
                  <td className="r num c-total strong">{money(net(l))}</td>
                  <td className="c-x">
                    <button
                      type="button"
                      className="icon-btn"
                      disabled={lines.length === 1}
                      onClick={() => onChange(lines.filter((x) => x.key !== l.key))}
                      aria-label="Remove line"
                    >
                      <Icon name="trash" size={15} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="lines-add">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => onChange([...lines, blankLine(Number(lines.at(-1)?.taxRate ?? 21))])}
        >
          <Icon name="plus" size={14} />
          Add line
        </button>
      </div>
      {zeroVat && (
        <p className="hint" style={{ margin: "0 20px" }}>
          {zeroVat}
        </p>
      )}
      <Totals lines={lines.map((l) => ({ net: net(l), taxRate: rate(l) }))} />
    </>
  );
}

export function Totals({ lines, paid }: { lines: { net: number; taxRate: number }[]; paid?: number }) {
  const sub = lines.reduce((s, l) => s + l.net, 0);
  const byRate: Record<number, number> = {};
  for (const l of lines) byRate[l.taxRate] = (byRate[l.taxRate] ?? 0) + Math.round(l.net * l.taxRate) / 100;
  const tax = Object.values(byRate).reduce((a, b) => a + b, 0);
  return (
    <div className="totals">
      <div className="kv-row">
        <span>Subtotal</span>
        <b className="num">{money(sub)}</b>
      </div>
      {Object.entries(byRate)
        .filter(([, v]) => v)
        .map(([r, v]) => (
          <div key={r} className="kv-row">
            <span>VAT {r}%</span>
            <b className="num">{money(v)}</b>
          </div>
        ))}
      <div className="kv-row grand">
        <span>Total</span>
        <b className="num">{money(sub + tax)}</b>
      </div>
      {paid !== undefined && paid > 0 && (
        <>
          <div className="kv-row">
            <span>Paid</span>
            <b className="num">−{money(paid)}</b>
          </div>
          <div className="kv-row">
            <span>Balance due</span>
            <b className="num">{money(sub + tax - paid)}</b>
          </div>
        </>
      )}
    </div>
  );
}

/** Progress through a document's life cycle. Partial states light the step in teal. */
export function Stepper({
  flow,
  status,
  partial,
}: {
  flow: { key: string; label: string }[];
  status: string;
  partial?: Record<string, string>;
}) {
  const effective = partial?.[status] ?? status;
  const cur = flow.findIndex((f) => f.key === effective);
  const isPartial = !!partial?.[status];
  const lastDone = cur === flow.length - 1 && !isPartial;
  return (
    <ol className="stepper">
      {flow.map((f, i) => {
        const done = i < cur || (i === cur && lastDone);
        const cls = done ? "done" : i === cur ? (isPartial ? "current partial" : "current") : "";
        return (
          <li key={f.key} className={cls}>
            <span className="dot">{done ? <Icon name="check" size={12} /> : i + 1}</span>
            <span>
              {f.label}
              {i === cur && isPartial ? " (part)" : ""}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export function Aside({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className="card">
      <div className="row-between" style={{ marginBottom: 10 }}>
        <h3 className="card-title" style={{ margin: 0 }}>
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

export function Linked({
  items,
}: {
  items: { to: string; number: string; kind: string; status: ReactNode }[];
}) {
  return (
    <ul className="plain-list" style={{ marginTop: 0 }}>
      {items.map((r) => (
        <li key={r.to}>
          <Link to={r.to}>
            <span className="mono">{r.number}</span> <span className="muted">{r.kind}</span>
          </Link>
          {r.status}
        </li>
      ))}
    </ul>
  );
}

export function JournalBlock({
  entries,
}: {
  entries: {
    id: string;
    number: string;
    date: string;
    memo: string;
    lines: { accountCode: string; accountName?: string | null; debit: number; credit: number }[];
  }[];
}) {
  return (
    <>
      {entries.map((j) => (
        <div className="je" key={j.id}>
          <div className="je-head">
            <span>{j.memo}</span>
            <span className="muted">{j.date}</span>
          </div>
          <table className="je-lines">
            <tbody>
              {j.lines.map((l) => (
                <tr key={`${l.accountCode}-${l.debit}-${l.credit}`}>
                  <td className="mono muted">{l.accountCode}</td>
                  <td>{l.accountName}</td>
                  <td className="r num">{l.debit ? moneyBase(l.debit) : ""}</td>
                  <td className="r num">{l.credit ? moneyBase(l.credit) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </>
  );
}

export function ReadLines({
  lines,
  cols,
}: {
  lines: {
    key: string;
    desc: ReactNode;
    sku?: string | null;
    qty: number;
    extra?: ReactNode;
    price: number;
    disc: number;
    tax: number;
    net: number;
  }[];
  cols?: ReactNode;
}) {
  return (
    <div className="lines-wrap">
      <table className="lines">
        <thead>
          <tr>
            <th className="c-prod">Item</th>
            <th className="r c-qty">Qty</th>
            {cols}
            <th className="r c-price">Unit</th>
            <th className="r c-tax">VAT</th>
            <th className="r c-total">Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.key}>
              <td className="c-prod">
                <div className="strong">{l.desc}</div>
                {l.sku && (
                  <Link className="mono muted sm" to={`/products?q=${l.sku}`}>
                    {l.sku}
                  </Link>
                )}
              </td>
              <td className="r num">{qty(l.qty)}</td>
              {l.extra}
              <td className="r num">
                {money(l.price)}
                {l.disc ? <div className="sm muted">−{l.disc}%</div> : null}
              </td>
              <td className="r num muted">{l.tax}%</td>
              <td className="r num strong">{money(l.net)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
